import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Dropdown,
    Form,
    Grid,
    Header,
    Icon,
    Label,
    Message,
    Modal,
    Pagination,
    Segment,
    Table,
    Popup, SemanticCOLORS
} from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    EProgrammingStatus,
    EProgrammingTravelType,
    Programming,
    ProgrammingFilter, statusOptions,
    travelTypeOptions,
    UpdateProgrammingRequest,
} from "../../models/programming";
import { SessionUser } from "../../models/session-user";
import { useBlockUI } from "../../shared/services/block-ui-service";
import useProgrammingService from "../../services/backoffice/hooks/useProgrammingService";
import SessionStorage from "../../store/session";
import { ToasterConfig } from "../../config/ToasterConfig";
import StringUtils from "../../shared/utils/string-utils";
import { ShortDto } from "../../models/short-dto";
import useVehiclePlateService from "../../services/backoffice/hooks/useVehiclePlateService";
import useClientService from "../../services/backoffice/hooks/useClientService";
import { ProgrammingLocationDto } from "../../models/programming-location-dto";
import api from "../../services";
import ProgrammingLocationRefreshButton from "../programming-location-refresh-button";
import { isStatusInGroup, TorreStatusGroup, getStatusLabelColor, isDelayedStatus, getProgrammingScheduleDisplay, ScheduleDeviationTone, getTrackingStatusLabel } from "../../shared/utils/torre-tracking-utils";
import ProgrammingDetailsModal from "./ProgrammingDetailsModal";
import {
    COMPACT_COLUMN_COUNT,
    COMPACT_COLUMN_WIDTHS,
    COMPACT_PAGE_SIZE,
    COMPACT_TABLE_MIN_WIDTH,
} from "./programming-list-compact";
import {
    SPREADSHEET_PAGE_SIZE,
} from "./programming-list-spreadsheet";
import ProgrammingSpreadsheetTable, { SpreadsheetLookups } from "./ProgrammingSpreadsheetTable";
import { TorreListMode } from "../../shared/utils/torre-list-mode";
import {
    formatProgrammingDateTime,
    formatProgrammingRoute,
    getCompactStageColumns,
} from "../../utils/programming-timeline-utils";
import './torre-table-ux.css';
import TorreTableToolbar, { TorreTableDensity } from './TorreTableToolbar';
import {
    filterTorreRowsByQuickSearch,
    sortTorreCompactRows,
    SortDirection,
    TorreCompactSortKey,
} from './torre-table-sort';
import ProgrammingTorreQuickEditModal from './ProgrammingTorreQuickEditModal';
import {
    canEditProgrammingRow,
    PROGRAMMING_EDIT_DENIED_TOOLTIP,
} from './programming-edit-access';
import { isSpreadsheetTerminalStatus } from './programming-list-spreadsheet';
import {
    buildTorreOperationalSections,
    buildPlatesWithActiveProgramming,
    getTorreArrivalIndicator,
    sortTorreOperationalRows,
    TORRE_OPERATIONAL_ORDER_BY,
    TorreOperationalHighlightColumn,
    TorreOperationalSection,
    usesTorreOperationalOrder,
} from './torre-operational-groups';

export interface ProgrammingListDemoSession {
    profile: string;
    userId?: string;
    userEmail?: string;
    managerClientId?: string;
}

export interface ProgrammingListDemoOverride {
    programmings: Programming[];
    totalPages?: number;
    loading?: boolean;
    lookups?: SpreadsheetLookups;
    /** Quando true, aplica regras reais de permissão mesmo em demo (screenshots / QA). */
    enforceEditPolicy?: boolean;
    demoSession?: ProgrammingListDemoSession;
}

interface ProgrammingListProps {
    isCompact?: boolean;
    listMode?: TorreListMode;
    onSelect?: (programming: ProgrammingLocationDto) => void;
    showFilters?: boolean;
    filter?: ProgrammingFilter;
    onFilterChange?: (filter: ProgrammingFilter) => void;
    statusGroupFilter?: TorreStatusGroup;
    /** Incrementar para forçar recarga (ex.: após criar programação na Torre). */
    refreshKey?: number;
    selectedProgrammingId?: string | null;
    showTorreTableToolbar?: boolean;
    showDensityToggle?: boolean;
    tableUxVariant?: 'default' | 'enhanced';
    /** Ordenação padrão da Torre (ex.: torre_operational). */
    defaultOrderBy?: string;
    canInlineEdit?: boolean;
    isManager?: boolean;
    lockedClientId?: string;
    spreadsheetLookups?: SpreadsheetLookups;
    /** Dados locais — pula API (rota /dev/torre-table-demo). */
    demoOverride?: ProgrammingListDemoOverride;
    /** Contagem de canceladas (visão Torre — grupo recolhido). */
    torreCancelledCount?: number;
}

const COMPACT_HEADER_STYLE: React.CSSProperties = {
    whiteSpace: 'nowrap',
    fontSize: '0.85em',
};

const CompactHeaderCell: React.FC<{
    label: string;
    title: string;
    sortKey?: TorreCompactSortKey;
    activeSort?: TorreCompactSortKey;
    sortDirection?: SortDirection;
    onSort?: (key: TorreCompactSortKey) => void;
    stickyFirst?: boolean;
}> = ({ label, title, sortKey, activeSort, sortDirection, onSort, stickyFirst }) => {
    const sortable = sortKey && onSort;
    const isActive = sortKey && activeSort === sortKey;
    const sortIcon = isActive
        ? (sortDirection === 'asc' ? 'sort up' : 'sort down')
        : 'sort';

    const cell = (
        <Table.HeaderCell
            style={COMPACT_HEADER_STYLE}
            className={[
                'torre-table-sticky-header-cell',
                stickyFirst ? 'torre-table-sticky-first-col' : '',
                sortable ? 'torre-table-sortable-header' : '',
            ].filter(Boolean).join(' ')}
            onClick={sortable ? () => onSort(sortKey) : undefined}
        >
            {label}
            {sortable && (
                <Icon name={sortIcon as 'sort'} style={{ marginLeft: 4, opacity: isActive ? 1 : 0.35 }} />
            )}
        </Table.HeaderCell>
    );

    return (
        <Popup content={title} trigger={cell} disabled={!title} />
    );
};

interface Justification {
    id: string;
    code: string;
    description: string;
}

const resolvePageSize = (compact: boolean, mode: TorreListMode) => {
    if (mode === 'spreadsheet') return SPREADSHEET_PAGE_SIZE;
    return compact ? COMPACT_PAGE_SIZE : 10;
};

const ProgrammingList: React.FC<ProgrammingListProps> = ({
    isCompact = false,
    listMode = 'compact',
    onSelect,
    showFilters: initialShowFilters,
    filter: externalFilter,
    onFilterChange,
    statusGroupFilter = 'todos',
    refreshKey = 0,
    selectedProgrammingId = null,
    showTorreTableToolbar = false,
    showDensityToggle = false,
    tableUxVariant = 'default',
    defaultOrderBy,
    canInlineEdit = false,
    isManager = false,
    lockedClientId,
    spreadsheetLookups,
    demoOverride,
    torreCancelledCount = 0,
}) => {
    const isDemo = Boolean(demoOverride);
    const torreUxEnabled = tableUxVariant === 'enhanced' || showTorreTableToolbar;
    const [programmings, setProgrammings] = useState<Programming[]>([]);
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const [filter, setFilter] = useState<ProgrammingFilter>(() => ({
        ...externalFilter,
        page: externalFilter?.page ?? 1,
        perPage: resolvePageSize(isCompact, listMode),
        orderBy: externalFilter?.orderBy ?? defaultOrderBy,
    }));
    const [savingId, setSavingId] = useState<string | null>(null);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [listTruncated, setListTruncated] = useState(false);
    const [listTotalCount, setListTotalCount] = useState(0);
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const blockUI = useBlockUI();
    const {
        getProgrammings,
        deleteProgramming,
        cancelProgramming,
        startProgramming,
        completeProgramming,
        updateProgramming,
    } = useProgrammingService();

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedJustification, setSelectedJustification] = useState<string>('');
    const [justifications, setJustifications] = useState<Justification[]>([]);
    const [selectedProgramming, setSelectedProgramming] = useState<Programming>();
    const [vehiclePlates, setVehiclePlates] = useState<ShortDto[]>([]);
    const [clients, setClients] = useState<ShortDto[]>([]);
    const { getAllVehiclePlatesShortDto } = useVehiclePlateService();
    const { getAllClientsShortDto } = useClientService();
    const [showFilters, setShowFilters] = useState(initialShowFilters ?? !isCompact);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [detailsProgramming, setDetailsProgramming] = useState<Programming | null>(null);
    const [quickSearch, setQuickSearch] = useState('');
    const [compactSortKey, setCompactSortKey] = useState<TorreCompactSortKey>('prevCheg');
    const [compactSortDir, setCompactSortDir] = useState<SortDirection>('asc');
    const [tableDensity, setTableDensity] = useState<TorreTableDensity>('comfortable');
    const [manualSortActive, setManualSortActive] = useState(false);
    const [torreCancelledExpanded, setTorreCancelledExpanded] = useState(false);
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');
    const [exporting, setExporting] = useState(false);
    const [editModalProgramming, setEditModalProgramming] = useState<Programming | null>(null);
    const [editModalSaving, setEditModalSaving] = useState(false);

    const openDetailsModal = (programming: Programming, event?: React.MouseEvent) => {
        event?.stopPropagation();
        setDetailsProgramming(programming);
        setDetailsModalOpen(true);
    };

    useEffect(() => {
        blockUI?.setActive(loading);
    }, [loading]);

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        if (isDemo) {
            setLoading(demoOverride?.loading ?? false);
            setProgrammings(demoOverride?.programmings ?? []);
            setTotalPages(demoOverride?.totalPages ?? 1);
            return;
        }
        listProgrammings();
    }, [
        filter?.page,
        filter?.perPage,
        filter?.status,
        filter?.clientId,
        filter?.mainVehiclePlateId,
        filter?.travelType,
        filter?.orderBy,
        filter?.startDate,
        filter?.endDate,
        filter?.torreWindowMode,
        filter?.torreTerminalLookbackDays,
        filter?.includeTorreCancelled,
        filter?.originReferenceId,
        filter?.destinationReferenceId,
        refreshKey,
        isDemo,
        demoOverride?.programmings,
        demoOverride?.loading,
        demoOverride?.totalPages,
    ]);

    useEffect(() => {
        if (!externalFilter) return;

        setFilter(prev => {
            const merged: ProgrammingFilter = {
                ...prev,
                ...externalFilter,
                page: externalFilter.page ?? prev.page ?? 1,
                perPage: resolvePageSize(isCompact, listMode),
            };

            return JSON.stringify(prev) === JSON.stringify(merged) ? prev : merged;
        });
    }, [externalFilter, isCompact, listMode]);

    useEffect(() => {
        setFilter(prev => {
            const perPage = resolvePageSize(isCompact, listMode);
            if (prev.perPage === perPage) return prev;
            return { ...prev, perPage, page: 1 };
        });
    }, [listMode, isCompact]);

    useEffect(() => {
        if (!isCompact) return;
        setFilter(prev => ({ ...prev, page: 1 }));
    }, [statusGroupFilter, isCompact]);

    async function listProgrammings() {
        try {
            setLoading(true);
            const response = await getProgrammings(filter);
            setTotalPages(response.data.totalPages);
            setProgrammings(response.data.itens);
            setListTruncated(Boolean(response.data.truncated));
            setListTotalCount(response.data.count ?? response.data.itens?.length ?? 0);
        } catch (error: any) {
            toast.warn('Falha ao listar programações', ToasterConfig.error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (isDemo) return;
        async function fetchVehiclePlates() {
            try {
                const response = await getAllVehiclePlatesShortDto();
                setVehiclePlates(response || []);
            } catch (error: any) {
                toast.warn('Falha ao buscar placas de veículos', ToasterConfig.error);
                setError(error.message);
            }
        }
        fetchVehiclePlates();
    }, [isDemo]);

    useEffect(() => {
        if (isDemo) return;
        async function fetchClients() {
            try {
                const response = await getAllClientsShortDto();
                setClients(response || []);
            } catch (error: any) {
                toast.warn('Falha ao buscar clientes', ToasterConfig.error);
                setError(error.message);
            }
        }
        fetchClients();
    }, [isDemo]);

    useEffect(() => {
        if (isDemo) return;
        fetchJustifications();
    }, [isDemo]);

    const fetchJustifications = async () => {
        try {
            const response = await api.get('/v1/backoffice/justifications/short');
            setJustifications(response.data?.data || []);
        } catch (error) {
            toast.error('Erro ao carregar justificativas');
            setJustifications([]);
        }
    };

    const handleDelete = async (programming: Programming) => {
        try {
            const result = window.confirm(`Tem certeza que deseja excluir a programação para ${programming.clientName}?`);
            if (!result) return;

            setLoading(true);
            await deleteProgramming(programming.id);
            toast.success('Programação excluída com sucesso', ToasterConfig.success);
            listProgrammings();
        } catch (error: any) {
            toast.warn('Falha ao excluir programação', ToasterConfig.error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const openCancelModal = (programming: Programming) => {
        setSelectedProgramming(programming);
        setShowCancelModal(true);
    };

    const handleCancel = async () => {
        if (!selectedProgramming || !selectedJustification) return;

        try {
            setLoading(true);
            await cancelProgramming(selectedProgramming.id, selectedJustification);
            toast.success('Programação cancelada com sucesso', ToasterConfig.success);
            setShowCancelModal(false);
            setSelectedJustification('');
            listProgrammings();
        } catch (error: any) {
            toast.warn('Falha ao cancelar programação', ToasterConfig.error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleStart = async (programming: Programming) => {
        try {
            const result = window.confirm(`Deseja iniciar a programação para ${programming.clientName}?`);
            if (!result) return;

            setLoading(true);
            await startProgramming(programming.id);
            toast.success('Programação iniciada com sucesso', ToasterConfig.success);
            listProgrammings();
        } catch (error: any) {
            toast.warn('Falha ao iniciar programação', ToasterConfig.error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleComplete = async (programming: Programming) => {
        try {
            const result = window.confirm(`Deseja concluir a programação para ${programming.clientName}?`);
            if (!result) return;

            setLoading(true);
            await completeProgramming(programming.id);
            toast.success('Programação concluída com sucesso', ToasterConfig.success);
            listProgrammings();
        } catch (error: any) {
            toast.warn('Falha ao concluir programação', ToasterConfig.error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (name: string, value: any) => {
        const newFilter = { ...filter, [name]: value, page: 1 };
        setFilter(newFilter);
        onFilterChange?.(newFilter);
    };

    const handleApplyFilters = () => {
        listProgrammings();
    };

    const handleClearFilters = () => {
        setFilter({ page: 1, perPage: resolvePageSize(isCompact, listMode) });
        listProgrammings();
    };

    const editContext = useMemo(() => {
        if (demoOverride?.demoSession) {
            return {
                profile: demoOverride.demoSession.profile,
                userId: demoOverride.demoSession.userId,
                userEmail: demoOverride.demoSession.userEmail,
                managerClientId: demoOverride.demoSession.managerClientId ?? lockedClientId,
            };
        }
        return {
            profile: sessionUser?.userData?.profile,
            userId: sessionUser?.userData?.id,
            userEmail: sessionUser?.userData?.email,
            managerClientId: lockedClientId,
        };
    }, [demoOverride?.demoSession, sessionUser, lockedClientId]);

    const programmingRowEditable = (programming: Programming) => {
        if (isDemo && canInlineEdit && !demoOverride?.enforceEditPolicy) {
            return !isSpreadsheetTerminalStatus(programming.status);
        }
        return canEditProgrammingRow({
            ...editContext,
            programming,
        });
    };

    const openQuickEdit = (programming: Programming, event?: React.MouseEvent) => {
        event?.stopPropagation();
        setEditModalProgramming(programming);
    };

    const handleQuickEditSave = async (id: string, payload: UpdateProgrammingRequest) => {
        setEditModalSaving(true);
        try {
            await handleInlineSave(id, payload);
            setEditModalProgramming(null);
        } finally {
            setEditModalSaving(false);
        }
    };

    const handleInlineSave = async (id: string, payload: UpdateProgrammingRequest) => {
        if (isDemo) {
            toast.info('Modo demo — alteração não persistida', ToasterConfig.success);
            return;
        }
        try {
            setSavingId(id);
            await updateProgramming(id, payload);
            toast.success('Programação atualizada', ToasterConfig.success);
            await listProgrammings();
        } catch (error: any) {
            toast.warn(error?.message || 'Falha ao atualizar programação', ToasterConfig.error);
            throw error;
        } finally {
            setSavingId(null);
        }
    };

    const handlePageChange = (_: any, { activePage }: any) => {
        setFilter(prev => ({ ...prev, page: activePage }));
    };

    const getStatusLabel = (status: string | number): string => getTrackingStatusLabel(status);

    const translateTravelTypeToPortuguese = (travelType: string): string => {
        switch (travelType) {
            case 'Empty':
                return 'Vazio';
            case 'Loaded':
                return 'Carregado';
            default:
                return 'Desconhecido';
        }
    };

    const deviationLabelColor = (tone: ScheduleDeviationTone): SemanticCOLORS => {
        switch (tone) {
            case 'late':
                return 'red';
            case 'early':
                return 'green';
            case 'onTime':
                return 'teal';
            default:
                return 'grey';
        }
    };

    const travelTypeColors: { [key: string]: SemanticCOLORS | undefined } = {
        Empty: 'grey',
        Loaded: 'blue',
    };

    const handleCompactSort = (key: TorreCompactSortKey) => {
        setManualSortActive(true);
        if (compactSortKey === key) {
            setCompactSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setCompactSortKey(key);
            setCompactSortDir('asc');
        }
        const nextFilter = { ...filter, orderBy: undefined, page: 1 };
        setFilter(nextFilter);
        onFilterChange?.(nextFilter);
    };

    const operationalSortActive = useMemo(
        () => torreUxEnabled && !manualSortActive && (
            usesTorreOperationalOrder(filter.orderBy)
            || (!filter.orderBy && defaultOrderBy === TORRE_OPERATIONAL_ORDER_BY)
        ),
        [torreUxEnabled, manualSortActive, filter.orderBy, defaultOrderBy]
    );

    const filteredByGroup = useMemo(
        () => programmings.filter(programming => isStatusInGroup(programming.status, statusGroupFilter)),
        [programmings, statusGroupFilter]
    );

    const visibleProgrammings = useMemo(() => {
        let rows = filterTorreRowsByQuickSearch(filteredByGroup, quickSearch);
        if (isDemo && operationalSortActive) {
            rows = sortTorreOperationalRows(rows, {
                platesWithActiveProgramming: buildPlatesWithActiveProgramming(programmings),
            });
        } else if (isCompact && torreUxEnabled && manualSortActive) {
            rows = sortTorreCompactRows(rows, compactSortKey, compactSortDir);
        }
        return rows;
    }, [
        filteredByGroup,
        quickSearch,
        isCompact,
        torreUxEnabled,
        compactSortKey,
        compactSortDir,
        manualSortActive,
        operationalSortActive,
        isDemo,
        programmings,
    ]);

    const showTorreCancelledRows = Boolean(filter.includeTorreCancelled) || torreCancelledExpanded;

    const operationalSections: TorreOperationalSection[] = useMemo(() => {
        if (!operationalSortActive) return [];
        const baseRows = visibleProgrammings.length > 0 ? visibleProgrammings : [];
        const sections = buildTorreOperationalSections(baseRows, {
            showCancelled: showTorreCancelledRows,
            ctx: { platesWithActiveProgramming: buildPlatesWithActiveProgramming(programmings) },
        });
        if (!showTorreCancelledRows && torreCancelledCount > 0) {
            sections.push({
                key: 'canceladas',
                label: 'Canceladas',
                highlightColumn: 'none',
                rows: [],
                collapsible: true,
                defaultCollapsed: true,
            });
        }
        return sections;
    }, [
        operationalSortActive,
        visibleProgrammings,
        showTorreCancelledRows,
        torreCancelledCount,
        programmings,
    ]);

    const renderTooltip = (content: string, children: JSX.Element) => (
        <Popup
            content={content}
            trigger={children}
            position="top center"
            size="small"
            inverted
        />
    );

    const renderTableHeaders = () => {
        if (isCompact) {
            const sortProps = torreUxEnabled
                ? {
                    activeSort: compactSortKey,
                    sortDirection: compactSortDir,
                    onSort: handleCompactSort,
                }
                : {};
            return (
                <Table.Row>
                    <CompactHeaderCell label="Placa" title="Placa cavalo mecânico" sortKey="placa" stickyFirst {...sortProps} />
                    <CompactHeaderCell label="Rota" title="Origem → Destino → Retorno" />
                    <CompactHeaderCell label="Prev. saída" title="Saída prevista — origem" />
                    <CompactHeaderCell label="Prev. cheg." title="Chegada prevista — destino" sortKey="prevCheg" {...sortProps} />
                    <CompactHeaderCell label="Real saída" title="Saída real — origem" />
                    <CompactHeaderCell label="Real cheg." title="Chegada real — destino" />
                    <CompactHeaderCell label="Prazo" title="Desvio em relacao ao previsto (atraso, adiantado ou tempo restante)" sortKey="prazo" {...sortProps} />
                    <CompactHeaderCell label="Status" title="Status da programação" sortKey="status" {...sortProps} />
                    <Table.HeaderCell
                        textAlign="center"
                        style={COMPACT_HEADER_STYLE}
                        className="torre-table-sticky-header-cell"
                    >
                        Ações
                    </Table.HeaderCell>
                </Table.Row>
            );
        }

        return (
            <Table.Row>
                <Table.HeaderCell>Cliente</Table.HeaderCell>
                <Table.HeaderCell>Placa CM</Table.HeaderCell>
                <Table.HeaderCell>Produto</Table.HeaderCell>
                <Table.HeaderCell>Volume</Table.HeaderCell>
                <Table.HeaderCell>Origem</Table.HeaderCell>
                <Table.HeaderCell>Destino</Table.HeaderCell>
                <Table.HeaderCell>Retorno</Table.HeaderCell>
                <Table.HeaderCell>Data Prevista</Table.HeaderCell>
                <Table.HeaderCell>Tipo</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
            </Table.Row>
        );
    };

    const renderCompactHoverContent = (programming: Programming) => {
        const route = formatProgrammingRoute(programming);
        const stages = getCompactStageColumns(programming, { compact: true });

        return (
            <div style={{ fontSize: '0.9em', lineHeight: 1.55, minWidth: 220 }}>
                <div><strong>Motorista:</strong> {programming.driverName || '—'}</div>
                <div><strong>Cliente:</strong> {programming.clientName || '—'}</div>
                <div><strong>Rota:</strong> {route.full}</div>
                <div><strong>Status:</strong> {getStatusLabel(programming.status)}</div>
                <div><strong>Prev. saída origem:</strong> {stages.expectedOriginDeparture}</div>
                <div><strong>Prev. chegada destino:</strong> {stages.expectedDestinationArrival}</div>
                <div><strong>Real saída origem:</strong> {stages.actualOriginDeparture}</div>
                <div><strong>Real chegada destino:</strong> {stages.actualDestinationArrival}</div>
            </div>
        );
    };

    const highlightCellClass = (
        column: 'prevSaida' | 'prevCheg',
        highlight?: TorreOperationalHighlightColumn
    ) => (highlight === column ? 'torre-col-highlight' : undefined);

    const handleExpandCancelled = () => {
        setTorreCancelledExpanded(true);
        const nextFilter = { ...filter, includeTorreCancelled: true, page: 1 };
        setFilter(nextFilter);
        onFilterChange?.(nextFilter);
    };

    const renderOperationalGroupHeader = (section: TorreOperationalSection) => {
        const count = section.key === 'canceladas' && section.rows.length === 0
            ? torreCancelledCount
            : section.rows.length;
        const isCollapsedCancelled = section.key === 'canceladas' && section.rows.length === 0 && count > 0;

        return (
            <Table.Row
                key={`group-${section.key}`}
                className="torre-group-header-row"
                onClick={isCollapsedCancelled ? handleExpandCancelled : undefined}
                style={isCollapsedCancelled ? { cursor: 'pointer' } : undefined}
            >
                <Table.Cell colSpan={String(COMPACT_COLUMN_COUNT)}>
                    {section.label} • {count}
                    {isCollapsedCancelled && (
                        <span style={{ marginLeft: 8, color: '#6b7280' }}>(clique para expandir)</span>
                    )}
                    {section.key === 'chegaram' && section.rows.length > 0 && (
                        <span style={{ marginLeft: 8, fontWeight: 500, color: '#047857' }}>
                            ↳ chegada real vs prevista
                        </span>
                    )}
                    {section.highlightColumn === 'prevSaida' && (
                        <span style={{ marginLeft: 8, fontWeight: 500, color: '#047857' }}>↳ Prev. saída</span>
                    )}
                    {section.highlightColumn === 'prevCheg' && (
                        <span style={{ marginLeft: 8, fontWeight: 500, color: '#047857' }}>↳ Prev. cheg.</span>
                    )}
                </Table.Cell>
            </Table.Row>
        );
    };

    const handleExportExcel = async () => {
        if (!exportStartDate || !exportEndDate) {
            toast.warn('Informe o período inicial e final para exportar.', ToasterConfig.warning);
            return;
        }
        try {
            setExporting(true);
            const response = await api.get('/v1/backoffice/programmings/export', {
                params: {
                    ...filter,
                    startDate: exportStartDate,
                    endDate: exportEndDate,
                    page: 1,
                    perPage: 0,
                },
                responseType: 'blob',
            });
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `programacoes-${exportStartDate.slice(0, 10)}-${exportEndDate.slice(0, 10)}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
            toast.success('Exportação concluída.', ToasterConfig.success);
        } catch {
            toast.error('Não foi possível exportar. Verifique o período (máx. 90 dias).', ToasterConfig.error);
        } finally {
            setExporting(false);
        }
    };

    const renderTableRow = (
        programming: Programming,
        highlight?: TorreOperationalHighlightColumn,
        sectionKey?: TorreOperationalSection['key']
    ) => {
        if (isCompact) {
            const stageColumns = getCompactStageColumns(programming, { compact: true });
            const route = formatProgrammingRoute(programming);
            const cellNowrap: React.CSSProperties = {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            };

            const schedule = getProgrammingScheduleDisplay(programming);
            const arrivalIndicator = sectionKey === 'chegaram'
                ? getTorreArrivalIndicator(programming)
                : null;
            const delayed = isDelayedStatus(programming.status);

            const isSelected = selectedProgrammingId === programming.id;
            const rowClass = [
                delayed ? 'torre-row--delayed' : '',
                isSelected ? 'torre-row--selected' : '',
            ].filter(Boolean).join(' ');

            return (
                <Table.Row
                    key={programming.id}
                    className={rowClass || undefined}
                    onClick={() => onSelect?.({
                        ...(programming as unknown as ProgrammingLocationDto),
                        currentLatitude: programming.currentLatitude,
                        currentLongitude: programming.currentLongitude,
                        currentAddressInfo: programming.currentAddressInfo,
                        expectedArrivalTime: programming.expectedArrivalTime,
                        expectedDepartureTime: programming.expectedDepartureTime,
                        expectedOriginDepartureAt: programming.expectedOriginDepartureAt,
                        expectedDestinationArrivalAt: programming.expectedDestinationArrivalAt,
                        expectedDestinationDepartureAt: programming.expectedDestinationDepartureAt,
                        expectedReturnArrivalAt: programming.expectedReturnArrivalAt,
                        actualOriginExitedAt: programming.actualOriginExitedAt,
                        actualDestinationEnteredAt: programming.actualDestinationEnteredAt,
                        actualDestinationExitedAt: programming.actualDestinationExitedAt,
                        actualReturnEnteredAt: programming.actualReturnEnteredAt,
                        isGoAndReturn: programming.isGoAndReturn ?? programming.isReturn,
                        productName: programming.productName,
                        volume: programming.volume,
                        travelType: String(programming.travelType),
                    })}
                    style={{ cursor: onSelect ? 'pointer' : 'default' }}
                >
                    <Table.Cell style={cellNowrap} className={torreUxEnabled ? 'torre-table-sticky-first-col' : undefined}>
                        <Popup
                            hoverable
                            wide
                            position="right center"
                            content={renderCompactHoverContent(programming)}
                            trigger={<span>{programming.mainVehiclePlateName || '-'}</span>}
                        />
                    </Table.Cell>
                    <Table.Cell style={cellNowrap}>
                        <Popup
                            content={route.full}
                            trigger={<span>{route.short}</span>}
                        />
                    </Table.Cell>
                    <Table.Cell
                        style={cellNowrap}
                        className={highlightCellClass('prevSaida', highlight)}
                    >
                        {stageColumns.expectedOriginDeparture}
                    </Table.Cell>
                    <Table.Cell
                        style={cellNowrap}
                        className={highlightCellClass('prevCheg', highlight)}
                    >
                        {stageColumns.expectedDestinationArrival}
                    </Table.Cell>
                    <Table.Cell style={cellNowrap}>{stageColumns.actualOriginDeparture}</Table.Cell>
                    <Table.Cell style={cellNowrap}>{stageColumns.actualDestinationArrival}</Table.Cell>
                    <Table.Cell style={cellNowrap}>
                        {arrivalIndicator && arrivalIndicator.tone !== 'unknown' ? (
                            <Label
                                color={arrivalIndicator.tone === 'late' ? 'red' : arrivalIndicator.tone === 'early' ? 'blue' : 'green'}
                                size="tiny"
                            >
                                {arrivalIndicator.label}
                            </Label>
                        ) : (
                            <Label color={deviationLabelColor(schedule.tone)} size="tiny">
                                {schedule.text}
                            </Label>
                        )}
                    </Table.Cell>
                    <Table.Cell style={cellNowrap}>
                        <Label color={getStatusLabelColor(programming.status)} size="tiny">
                            {getStatusLabel(programming.status)}
                        </Label>
                    </Table.Cell>
                    <Table.Cell
                        textAlign="center"
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={(event: React.MouseEvent) => event.stopPropagation()}
                    >
                        {renderTooltip(
                            programmingRowEditable(programming) ? 'Editar programação' : PROGRAMMING_EDIT_DENIED_TOOLTIP,
                            <Button
                                type="button"
                                size="mini"
                                color="blue"
                                icon="edit"
                                disabled={!programmingRowEditable(programming)}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (programmingRowEditable(programming)) openQuickEdit(programming, event);
                                }}
                            />
                        )}
                        <Button
                            type="button"
                            size="mini"
                            color="teal"
                            icon="eye"
                            title="Ver detalhes"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                openDetailsModal(programming, event);
                            }}
                        />
                    </Table.Cell>
                </Table.Row>
            );
        }

        return (
            <Table.Row key={programming.id}>
                <Table.Cell>{programming.clientName}</Table.Cell>
                <Table.Cell>{programming.mainVehiclePlateName || '-'}</Table.Cell>
                <Table.Cell>{programming.productName}</Table.Cell>
                <Table.Cell>{StringUtils.toBrazilianDecimalFormat(programming.volume)}</Table.Cell>
                <Table.Cell>{programming.originReferenceName}</Table.Cell>
                <Table.Cell>{programming.destinationReferenceName}</Table.Cell>
                <Table.Cell>{programming?.returnDestinationReferenceName || programming?.originReferenceName}</Table.Cell>
                <Table.Cell>
                    {programming.expectedDepartureTime
                        ? formatProgrammingDateTime(programming.expectedOriginDepartureAt ?? programming.expectedDepartureTime)
                        : '-'}
                </Table.Cell>
                <Table.Cell>
                    <Label color={travelTypeColors[programming.travelType] || 'grey'}>
                        {translateTravelTypeToPortuguese(programming.travelType.toString())}
                    </Label>
                </Table.Cell>
                <Table.Cell>
                    <Label color={getStatusLabelColor(programming.status)}>
                        {getStatusLabel(programming.status)}
                    </Label>
                </Table.Cell>
                <Table.Cell textAlign="center">
                    <Button.Group size="mini">
                        {renderTooltip("Ver detalhes",
                            <Button
                                icon="eye"
                                color="teal"
                                onClick={() => openDetailsModal(programming)}
                            />
                        )}
                        {renderTooltip("Editar",
                            <Button
                                icon="edit"
                                color="blue"
                                onClick={() => navigate(`/backoffice/programming/edit/${programming.id}`)}
                                disabled={[
                                    EProgrammingStatus.Completed,
                                    EProgrammingStatus.Cancelled,
                                    EProgrammingStatus.Loading,
                                    EProgrammingStatus.Unloading,
                                    EProgrammingStatus.EmptyTransit,
                                    EProgrammingStatus.LoadedTransit
                                ].includes(Number(programming.status))}
                            />
                        )}
                        {programming.status.toString() === 'Created' && (
                            renderTooltip("Iniciar",
                                <Button
                                    icon="play"
                                    color="green"
                                    onClick={() => handleStart(programming)}
                                />
                            )
                        )}
                        {[
                            EProgrammingStatus.InProgress,
                            EProgrammingStatus.Loading,
                            EProgrammingStatus.LoadedTransit,
                            EProgrammingStatus.EmptyTransit,
                            EProgrammingStatus.Unloading
                        ].includes(Number(programming.status)) && (
                            renderTooltip("Concluir",
                                <Button
                                    icon="check"
                                    color="green"
                                    onClick={() => handleComplete(programming)}
                                />
                            )
                        )}
                        {![
                            EProgrammingStatus.Completed,
                            EProgrammingStatus.Cancelled
                        ].includes(Number(programming.status)) && (
                            renderTooltip("Cancelar",
                                <Button
                                    icon="cancel"
                                    color="orange"
                                    onClick={() => openCancelModal(programming)}
                                />
                            )
                        )}
                        {renderTooltip("Excluir",
                            <Button
                                icon="trash"
                                color="red"
                                onClick={() => handleDelete(programming)}
                                disabled={[
                                    EProgrammingStatus.InProgress,
                                    EProgrammingStatus.Loading,
                                    EProgrammingStatus.LoadedTransit,
                                    EProgrammingStatus.EmptyTransit,
                                    EProgrammingStatus.Unloading
                                ].includes(Number(programming.status))}
                            />
                        )}
                    </Button.Group>
                </Table.Cell>
            </Table.Row>
        );
    };

    const formContent = (
        <Segment
            raised={!isCompact}
            style={isCompact ? {
                maxWidth: 'none',
                width: '100%',
                margin: 0,
                padding: 0,
                overflow: 'visible',
                boxShadow: 'none',
                border: 'none',
                background: 'transparent',
            } : {
                width: '100%',
                margin: '5px auto',
                padding: '20px',
            }}
        >
            {!isCompact && (
                <Header as="h3" textAlign="center" color="green">
                    Programações
                </Header>
            )}

            <Grid>
                {!isCompact && (
                    <Grid.Row>
                        <Grid.Column>
                            <Button
                                color="green"
                                icon="plus"
                                content="Nova Programação"
                                onClick={() => navigate('/backoffice/programming/create')}
                                floated="right"
                            />
                            <ProgrammingLocationRefreshButton
                                onRefreshComplete={() => {
                                    listProgrammings();
                                }}
                            />
                            <Button
                                color="green"
                                icon={showFilters ? "filter" : "filter"}
                                content={showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
                                onClick={toggleFilters}
                            />
                        </Grid.Column>
                    </Grid.Row>
                )}

                {showFilters && (
                    <Grid.Row>
                        <Grid.Column>
                            <Segment>
                                <Form>
                                    <Form.Group widths="equal">
                                        {!isCompact && (
                                            <>
                                                <Form.Field>
                                                    <label>Cliente</label>
                                                    <Dropdown
                                                        placeholder="Nome do cliente"
                                                        fluid
                                                        selection
                                                        search
                                                        clearable
                                                        options={clients}
                                                        value={filter.clientId || ''}
                                                        onChange={(_, { value }) => handleFilterChange('clientId', value)}
                                                    />
                                                </Form.Field>
                                                <Form.Field>
                                                    <label>Tipo de Deslocamento</label>
                                                    <Dropdown
                                                        placeholder="Selecione"
                                                        fluid
                                                        selection
                                                        options={travelTypeOptions}
                                                        value={filter.travelType !== undefined ? filter.travelType : ''}
                                                        onChange={(_, { value }) => handleFilterChange('travelType', value as EProgrammingTravelType)}
                                                    />
                                                </Form.Field>
                                            </>
                                        )}
                                        <Form.Field>
                                            <label>Placa CM</label>
                                            <Dropdown
                                                placeholder="Placa principal"
                                                fluid
                                                selection
                                                search
                                                clearable
                                                options={vehiclePlates}
                                                value={filter.mainVehiclePlateId || ''}
                                                onChange={(_, { value }) => handleFilterChange('mainVehiclePlateId', value)}
                                            />
                                        </Form.Field>
                                        <Form.Field>
                                            <label>Status</label>
                                            <Dropdown
                                                placeholder="Selecione"
                                                fluid
                                                selection
                                                options={statusOptions}
                                                value={filter.status !== undefined ? filter.status : ''}
                                                onChange={(_, { value }) => handleFilterChange('status', value)}
                                            />
                                        </Form.Field>
                                    </Form.Group>
                                    
                                    <Form.Group widths="equal">
                                        <Form.Field>
                                            <label>Exportar — início</label>
                                            <Form.Input
                                                type="date"
                                                value={exportStartDate}
                                                onChange={(_, { value }) => setExportStartDate(value)}
                                            />
                                        </Form.Field>
                                        <Form.Field>
                                            <label>Exportar — fim</label>
                                            <Form.Input
                                                type="date"
                                                value={exportEndDate}
                                                onChange={(_, { value }) => setExportEndDate(value)}
                                            />
                                        </Form.Field>
                                        <Form.Field>
                                            <label>&nbsp;</label>
                                            <Button
                                                color="teal"
                                                icon="file excel outline"
                                                content="Exportar Excel"
                                                loading={exporting}
                                                onClick={handleExportExcel}
                                            />
                                        </Form.Field>
                                    </Form.Group>

                                    <Form.Group widths="equal">
                                        <Form.Field>
                                            <label>Ordenar por</label>
                                            <Dropdown
                                                placeholder="Selecione a ordenação"
                                                fluid
                                                selection
                                                clearable
                                                options={[
                                                    { key: 'created_desc', text: 'Data de Criação (Mais recente)', value: 'created_desc' },
                                                    { key: 'created_asc', text: 'Data de Criação (Mais antiga)', value: 'created_asc' },
                                                    { key: 'status', text: 'Status', value: 'status' },
                                                    { key: 'departure_desc', text: 'Data de Saída (Mais recente)', value: 'departure_desc' },
                                                    { key: 'departure_asc', text: 'Data de Saída (Mais antiga)', value: 'departure_asc' }
                                                ]}
                                                value={filter.orderBy || ''}
                                                onChange={(_, { value }) => handleFilterChange('orderBy', value)}
                                            />
                                        </Form.Field>
                                    </Form.Group>

                                    <Button.Group size={isCompact ? "small" : "medium"}>
                                        <Button
                                            color="green"
                                            icon="search"
                                            content="Filtrar"
                                            onClick={handleApplyFilters}
                                        />
                                        <Button
                                            color="grey"
                                            icon="eraser"
                                            content="Limpar"
                                            onClick={handleClearFilters}
                                        />
                                    </Button.Group>
                                </Form>
                            </Segment>
                        </Grid.Column>
                    </Grid.Row>
                )}

                <Grid.Row>
                    <Grid.Column style={{ minWidth: 0 }}>
                        {isCompact && torreUxEnabled && listTruncated && listTotalCount > 0 && (
                            <Message warning size="small">
                                Mostrando {programmings.length} de {listTotalCount}. Refine o filtro para ver mais resultados.
                            </Message>
                        )}
                        {isCompact && torreUxEnabled && (
                            <TorreTableToolbar
                                search={quickSearch}
                                onSearchChange={setQuickSearch}
                                visibleCount={visibleProgrammings.length}
                                totalCount={filteredByGroup.length}
                                density={tableDensity}
                                onDensityChange={setTableDensity}
                                showDensityToggle={showDensityToggle}
                            />
                        )}
                        {listMode === 'spreadsheet' ? (
                            <ProgrammingSpreadsheetTable
                                torreUxEnhanced={torreUxEnabled}
                                selectedProgrammingId={selectedProgrammingId}
                                programmings={visibleProgrammings}
                                lookups={spreadsheetLookups ?? {
                                    vehiclePlates,
                                    clients,
                                    drivers: [],
                                    products: [],
                                    references: [],
                                }}
                                canInlineEdit={canInlineEdit}
                                isManager={isManager}
                                lockedClientId={lockedClientId}
                                savingId={savingId}
                                onSelect={onSelect}
                                onOpenDetails={openDetailsModal}
                                onOpenEdit={openQuickEdit}
                                editProfile={editContext.profile}
                                editUserId={editContext.userId}
                                editUserEmail={editContext.userEmail}
                                onSaveRow={handleInlineSave}
                                operationalSections={operationalSections.length > 0 ? operationalSections : undefined}
                            />
                        ) : (
                        <div
                            className={[
                                torreUxEnabled && isCompact ? 'torre-table-scroll' : '',
                            ].filter(Boolean).join(' ') || undefined}
                            style={!torreUxEnabled || !isCompact ? { overflowX: 'auto', width: '100%', maxWidth: '100%' } : undefined}
                        >
                        <Table
                            celled
                            striped
                            selectable
                            color="green"
                            compact={isCompact}
                            className={[
                                torreUxEnabled && isCompact ? 'torre-table-ux' : '',
                                torreUxEnabled && isCompact && tableDensity === 'dense' ? 'torre-table-ux--dense' : '',
                            ].filter(Boolean).join(' ') || undefined}
                            style={isCompact ? {
                                tableLayout: 'fixed',
                                width: '100%',
                                minWidth: COMPACT_TABLE_MIN_WIDTH,
                            } : undefined}
                        >
                            {isCompact && (
                                <colgroup>
                                    {COMPACT_COLUMN_WIDTHS.map((width, index) => (
                                        <col key={`compact-col-${index}`} style={{ width }} />
                                    ))}
                                </colgroup>
                            )}
                            <Table.Header>
                                {renderTableHeaders()}
                            </Table.Header>
                            <Table.Body>
                                {!visibleProgrammings || visibleProgrammings.length === 0 ? (
                                    <Table.Row>
                                        <Table.Cell
                                            colSpan={isCompact ? String(COMPACT_COLUMN_COUNT) : "11"}
                                            textAlign="center"
                                            className={torreUxEnabled ? 'torre-table-empty' : undefined}
                                        >
                                            {torreUxEnabled ? (
                                                <>
                                                    <Header as="h4" icon>
                                                        <Icon name="inbox" />
                                                        Nenhuma viagem nesta visão
                                                    </Header>
                                                    <p>
                                                        {quickSearch
                                                            ? 'Nenhum resultado para a busca. Tente outra placa, motorista ou rota.'
                                                            : 'Ajuste os filtros do monitor ou aguarde novas programações.'}
                                                    </p>
                                                </>
                                            ) : (
                                                'Nenhuma programação encontrada'
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                ) : operationalSections.length > 0 ? (
                                    operationalSections.flatMap(section => [
                                        renderOperationalGroupHeader(section),
                                        ...section.rows.map(row => renderTableRow(row, section.highlightColumn, section.key)),
                                    ])
                                ) : (
                                    visibleProgrammings.map(programming => renderTableRow(programming))
                                )}
                            </Table.Body>
                        </Table>
                        </div>
                        )}

                        {totalPages > 1 && (
                            <Pagination
                                size={isCompact ? "mini" : undefined}
                                style={{ marginTop: isCompact ? '0.75em' : undefined }}
                                activePage={filter.page}
                                onPageChange={handlePageChange}
                                totalPages={totalPages}
                                ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
                                firstItem={{ content: <Icon name="angle double left" />, icon: true }}
                                lastItem={{ content: <Icon name="angle double right" />, icon: true }}
                                prevItem={{ content: <Icon name="angle left" />, icon: true }}
                                nextItem={{ content: <Icon name="angle right" />, icon: true }}
                            />
                        )}
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </Segment>
    );

    return (
        <div>
            {formContent}
            <ProgrammingDetailsModal
                open={detailsModalOpen}
                programmingId={detailsProgramming?.id ?? null}
                initialProgramming={detailsProgramming ?? undefined}
                onClose={() => {
                    setDetailsModalOpen(false);
                    setDetailsProgramming(null);
                }}
            />
            {!isCompact && (
                <Modal
                    open={showCancelModal}
                    onClose={() => setShowCancelModal(false)}
                    size="small"
                >
                    <Modal.Header>Cancelar Programação</Modal.Header>
                    <Modal.Content>
                        <Form>
                            <Form.Field>
                                <label>Justificativa</label>
                                <Dropdown
                                    placeholder="Selecione uma justificativa"
                                    fluid
                                    selection
                                    options={justifications.map(j => ({
                                        key: j.id,
                                        text: j.description,
                                        value: j.id
                                    }))}
                                    value={selectedJustification}
                                    onChange={(_, { value }) => setSelectedJustification(value as string)}
                                    required
                                />
                            </Form.Field>
                        </Form>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button color="grey" onClick={() => setShowCancelModal(false)}>
                            Cancelar
                        </Button>
                        <Button
                            color="red"
                            onClick={handleCancel}
                            disabled={!selectedJustification}
                        >
                            Confirmar
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}
            {(isCompact || listMode === 'spreadsheet') && (
                <ProgrammingTorreQuickEditModal
                    open={Boolean(editModalProgramming)}
                    programming={editModalProgramming}
                    loading={editModalSaving}
                    lockedClientId={lockedClientId}
                    lookups={{
                        vehiclePlates: spreadsheetLookups?.vehiclePlates ?? vehiclePlates,
                        clients: spreadsheetLookups?.clients ?? clients,
                        drivers: spreadsheetLookups?.drivers ?? [],
                        products: spreadsheetLookups?.products ?? [],
                        references: spreadsheetLookups?.references ?? [],
                        routes: spreadsheetLookups?.routes ?? [],
                    }}
                    onClose={() => setEditModalProgramming(null)}
                    onSave={handleQuickEditSave}
                />
            )}
        </div>
    );
};

export default ProgrammingList;